import { MultiSelect, MultiSelectProps, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Column } from '../container/Container';
import { TextBodyExtraExtraSmall } from '../text/Texts';
import { cn } from '../ui/cn';
import { useState } from 'react';
import { FormElementProps } from '../inputs/InputController';
import { MdArrowDropDown } from 'react-icons/md';
import { inputStyle, labelStyle } from './styles';

interface MultiDropdownInputProps extends Omit<MultiSelectProps, 'onChange' | 'onBlur' | 'appendTo'>, FormElementProps {
	placeholder?: string;
	label?: string;
	appendTo?: 'default' | 'self'
}

const MultiDropdownInput = ({
	id,
	value,
	onChange,
	onBlur,
	error,

	label,
	placeholder,
	variant = 'filled',
	appendTo = 'self',
	...dropdownProps
}: MultiDropdownInputProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const needScroll = (dropdownProps.options?.length ?? 0) > 4;
	const hasValue = (value?.length ?? 0) > 0;
	const dropdownIcon = (
		<MdArrowDropDown
			size={24}
			className={cn(
				'text-onSurfaceVariant transition-transform duration-300',
				isExpanded ? 'rotate-0' : '-rotate-180'
			)}
		/>
	);

	return (
		<Column className={cn('w-full relative', dropdownProps.disabled && 'opacity-50')}>
			<MultiSelect
				unstyled
				{...dropdownProps}
				appendTo={appendTo === 'default' ? undefined : 'self'}
				id={id}
				value={value}
				onChange={(e: MultiSelectChangeEvent) => onChange?.(e.value)}
				dropdownIcon={dropdownIcon}
				onShow={() => setIsExpanded(true)}
				onHide={() => {
					setIsExpanded(false);
					onBlur?.()
				}}
				showSelectAll={false}
				variant={undefined}
				maxSelectedLabels={2}
				selectedItemsLabel={value?.[0] ? `${value[0]} + ${value.length - 1} more` : undefined}
				checkboxIcon={(<img src="/images/svg/check_small.svg" className="absolute top-0 right-0" />)}
				pt={{
					root: { className: inputStyle({ variant, isFocus: isExpanded, error: !!error }) },
					trigger: { className: 'absolute top-4 right-3' },
					panel: { className: cn(
						'bg-surfaceContainer rounded-b-[4px]',
						'w-full max-h-[240px] py-2 blue-scroll', needScroll && 'pr-1',
						'shadow-[0px_2px_6px_2px_#00000026,0px_1px_2px_0px_#0000004D]',
					)},
					wrapper: {
						className: cn('overflow-y-auto', needScroll && 'pr-1'),
						style: { maxHeight: '224px' }
					},
					header: { className: 'hidden' },
					item: { className: cn(
						'font-poppins text-body-large text-onSurface',
						'h-14 flex gap-3 px-3 items-center',
						'focus-visible:outline-none hover:bg-[#D8DDE0]',
					)},
					label: { className: cn(!hasValue && 'hidden') },
					checkbox: {
						root: { className: 'relative flex h-6 w-6 justify-center items-center' },
						box: { className: cn(
							'h-[18px] w-[18px] rounded-[2px] border-onSurfaceVariant',
							'data-[p-highlight=false]:border-[2px] data-[p-highlight=true]:bg-primary',
						)},
						input: { className: 'hidden' },
					}
				}}
			/>

			{/* Placeholder */}
			{(variant === 'filled' && placeholder && !hasValue) && (
				<span
					children={placeholder}
					className={cn(
						'absolute left-4 top-6',
						'transition-opacity duration-300 pointer-events-none',
						'font-poppins text-body-large text-onSurfaceVariant',
						'opacity-0', isExpanded && 'opacity-100'
					)}
				/>
			)}

			{/* Label */}
			{label && (
				<label
					htmlFor={id}
					children={label}
					className={labelStyle({ variant, isFocus: isExpanded, hasValue, error: !!error })}
				/>
			)}

			{/* Error */}
			{(error && error.message) && (
				<TextBodyExtraExtraSmall children={error.message} className="pl-4 pt-1 text-error" />
			)}
		</Column>
	);
};

export default MultiDropdownInput;
